import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  type StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  type ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';

export interface DrawerComponentProps<T> {
  extendedState?: T;
  onClose: () => void;
  open: boolean;
}

export interface Props<T> {
  children: React.ReactNode;
  ContentComponent: React.ComponentType<DrawerComponentProps<T>>;
  drawerStyle?: StyleProp<ViewStyle>;
  extendedState?: T;
  fixed?: boolean;
  onClose: () => void;
  open: boolean;
  overlayStyle?: StyleProp<ViewStyle>;
  position?: 'left' | 'right';
  width: number;
}

export const Drawer = <T extends {}>({
  children,
  ContentComponent,
  fixed = false,
  onClose,
  open,
  position = 'left',
  width,
  extendedState,
  overlayStyle,
  drawerStyle,
}: Props<T>) => {
  const [styles] = useStyles();

  const openValueRef = useRef(new Animated.Value(open ? 1 : 0));
  const [showOverlay, setShowOverlay] = useState(open);

  useEffect(() => {
    if (open) {
      setShowOverlay(true);
    }

    Animated.timing(openValueRef.current, {
      duration: 500,
      easing: Easing.out(Easing.exp),
      toValue: open ? 1 : 0,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished && !open) {
        setShowOverlay(false);
      }
    });
  }, [open]);

  const isRight = position === 'right';
  return (
    <View style={[styles.base, isRight ? styles.baseRight : null]}>
      <View style={styles.content}>
        {children}
        {!fixed && showOverlay ? (
          <TouchableWithoutFeedback onPress={onClose}>
            <Animated.View
              style={[
                styles.overlay,
                {
                  opacity: openValueRef.current.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 0.75],
                  }),
                },
                overlayStyle,
              ]}
            />
          </TouchableWithoutFeedback>
        ) : null}
      </View>
      <Animated.View
        style={[
          styles.drawer,
          fixed
            ? styles.drawerFixed
            : {
                transform: [
                  {
                    translateX: openValueRef.current.interpolate({
                      inputRange: [0, 1],
                      outputRange: [isRight ? width : -width, 0],
                    }),
                  },
                ],
              },
          isRight ? styles.drawerRight : styles.drawerLeft,
          { width: width },
          drawerStyle,
        ]}
      >
        <ContentComponent
          extendedState={extendedState}
          onClose={onClose}
          open={open}
        />
      </Animated.View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    flexDirection: 'row-reverse',
    // Hide overflow to prevent drawers from adding extra horizontal space
    // to the page when they're not open
    overflow: 'hidden',
  },
  baseRight: {
    flexDirection: 'row',
  },
  content: {
    flex: 1,
  },
  drawer: {
    backgroundColor: theme.colors.white,
    bottom: 0,
    position: 'absolute',
    top: 0,
  },
  drawerFixed: {
    position: 'relative',
  },
  drawerLeft: {
    borderLeftWidth: 0,
    left: 0,
  },
  drawerRight: {
    borderRightWidth: 0,
    right: 0,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.colors.black,
  },
}));
